import { useState, Fragment } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import {
  HomeIcon, FolderIcon, TicketIcon, CurrencyDollarIcon, DocumentChartBarIcon,
  WrenchScrewdriverIcon, QuestionMarkCircleIcon,
  Bars3Icon, XMarkIcon, ChevronDownIcon, ArrowRightOnRectangleIcon,
  UserCircleIcon, BellIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import clsx from 'clsx';

const navigation = [
  { name: 'Dashboard', to: '/client', icon: HomeIcon, end: true },
  { name: 'Projects', to: '/client/projects', icon: FolderIcon },
  { name: 'Tickets', to: '/client/tickets', icon: TicketIcon },
  { name: 'Billing', to: '/client/billing', icon: CurrencyDollarIcon },
  { name: 'Reports', to: '/client/reports', icon: DocumentChartBarIcon },
  { name: 'Maintenance', to: '/client/maintenance', icon: WrenchScrewdriverIcon },
  { name: 'Help Center', to: '/client/help', icon: QuestionMarkCircleIcon },
];

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center h-16 px-6 border-b border-white/10">
        <img
          src={branding.logo_url}
          alt={branding.brand_name}
          className="h-8"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <span className="text-lg font-bold text-white hidden">{branding.brand_name}</span>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.to}
            end={item.end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) => clsx(
              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              isActive
                ? 'bg-[#5ba4f5]/10 text-[#5ba4f5]'
                : 'text-blue-200/60 hover:bg-white/5 hover:text-white'
            )}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      {!branding.hide_maintenova_branding && (
        <div className="px-6 py-3 border-t border-white/10 text-xs text-blue-200/30">
          Powered by Maintenova
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#060a1f]">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-[#0a0e2a] shadow-xl">
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 text-blue-200/40 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col bg-[#0a0e2a] border-r border-white/10">
        <SidebarContent />
      </div>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 sm:px-6 bg-[#0a0e2a]/80 backdrop-blur-md border-b border-white/10">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-blue-200/50 hover:text-white"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            <button className="relative p-2 text-blue-200/40 hover:text-white rounded-lg hover:bg-white/5">
              <BellIcon className="h-5 w-5" />
            </button>

            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/5 transition-colors">
                <div className="h-8 w-8 rounded-full bg-[#5ba4f5] flex items-center justify-center text-white text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-white truncate max-w-[120px]">{user?.name}</p>
                  <p className="text-xs text-blue-200/40">Client</p>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-blue-200/40 hidden sm:block" />
              </MenuButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="absolute right-0 mt-2 w-56 bg-[#0f1535] rounded-xl shadow-lg ring-1 ring-white/10 py-1 focus:outline-none">
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-blue-200/40 truncate">{user?.email}</p>
                  </div>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => navigate('/client/profile')}
                        className={clsx('flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-200/70', active ? 'bg-white/5 text-white' : '')}
                      >
                        <UserCircleIcon className="h-4 w-4 text-blue-200/40" />
                        Profile & Settings
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={clsx('flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400', active ? 'bg-red-500/10' : '')}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign out
                      </button>
                    )}
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
